// Centralized business metrics and data points
// This file contains all key numbers and claims used across the website
// Update values here to maintain consistency across all components

export const businessMetrics = {
  // Core Business Metrics
  deals: {
    totalCompleted: 50, // Total successful engagements completed
    totalValue: 250, // Total value in millions ($250M+)
    currentPipeline: 150, // Current pipeline value in millions ($150M+)
    exits: 13, // Specific exits/transactions closed
    displayText: '50+', // How to display in UI
  },

  // Buyer Network
  buyers: {
    totalDatabase: 200000, // Total qualified buyers in database
    displayText: '200k+', // How to display in UI
    qualifiedText: '200,000 qualified buyers', // For detailed descriptions
    curatedText: 'thousands of pre-qualified buyers', // For marketing copy
  },

  // Business Size Focus
  businessSize: {
    revenueRange: {
      min: 5, // Minimum revenue in millions
      max: 150, // Maximum revenue in millions
      sweetSpot: 50, // Sweet spot maximum for most detailed descriptions
      displayText: '$5M to $150M', // Primary display
      sweetSpotText: '$5M to $50M', // For specialized contexts
    },
    ebitdaThreshold: 5, // EBITDA threshold in millions for certain services
  },

  // Timeline Metrics
  timeline: {
    fullProcess: {
      months: 6, // Full process duration
      displayText: '6 months', // How to display
    },
    initialOffers: {
      weeks: 3, // Time to first offers
      displayText: '3 weeks', // How to display
      detailText: 'as little as 3 weeks', // More detailed version
    },
    loiAverage: {
      days: 18, // Average days to LOI
      displayText: '18 days', // How to display
    },
    quickResults: {
      weeks: 2, // For "under X weeks" claims
      maxWeeks: 3, // Maximum for range
      displayText: '2-3 weeks', // How to display
    }
  },

  // Performance Metrics
  performance: {
    valuationImprovement: {
      average: 35, // Average valuation improvement percentage
      displayText: '35%', // How to display
      range: '20-50%', // Range for detailed descriptions
    },
    higherValuations: {
      percentage: 35, // Higher valuations vs initial offers
      displayText: '35%', // How to display
      context: 'higher than initial offers', // Context for usage
    },
    competitiveUplift: {
      percentage: 50, // Maximum uplift from competitive dynamics
      displayText: '50%+', // How to display for maximum cases
      context: 'through competitive bidding', // Context
    },
    averageMultiple: {
      value: 2.3, // Average valuation improvement multiple
      displayText: '2.3x', // How to display
      context: 'average valuation improvement', // Context
    }
  },

  // Team and Experience
  team: {
    experience: {
      years: 30, // Individual team member experience (Tim's HVAC experience)
      collectiveYears: 85, // Collective years across team
      displayText: '50+ years', // How to display collective
    },
    availability: '24/7', // Team availability claim
  },

  // Service Metrics
  service: {
    confidentiality: '100%', // Confidentiality claim
    transparency: '100%', // Transparency claim
    successFee: true, // Success fee only model
    upfrontFees: false, // No upfront fees
  },

  // Industry Focus
  industries: {
    primary: ['Technology', 'Healthcare', 'Service Businesses'],
    technologySubset: ['SaaS', 'IT services', 'digital agencies'],
    healthcareSubset: ['medical practices', 'dental groups', 'healthcare services'],
    serviceSubset: ['commercial services', 'home services', 'professional services'],
  },

  // Buyer Types
  buyerTypes: {
    strategic: {
      premium: '20-50%', // Premium strategic buyers pay
      displayText: 'Strategic Buyers pay 20-50% premiums',
    },
    financial: {
      dryPowder: '50B+', // PE dry powder available
      displayText: '$50B+ in dry powder',
    },
    total: {
      interestedParties: '12-15', // Average interested parties per deal
      displayText: '12-15 average interested parties',
    }
  },

  // Process Phases
  process: {
    phases: 4, // Number of process phases
    totalWeeks: 24, // Total weeks (6 months)
    phaseDuration: 4, // Weeks per phase average
  }
};

// Helper functions for consistent formatting
export const formatters = {
  currency: (amount, inMillions = false) => {
    if (inMillions) {
      return `$${amount}M`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  },

  number: (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  },

  compact: (num) => {
    if (num >= 1000000) return `${Math.round(num / 1000000)}M`;
    if (num >= 1000) return `${Math.round(num / 1000)}k`;
    return `${num}`;
  },

  percentage: (num) => `${num}%`,

  range: (min, max, unit = '') => `${unit}${min} to ${unit}${max}`,
};

// Validation function to ensure consistency
export const validateMetrics = () => {
  const issues = [];
  
  // Add validation rules here as needed
  if (businessMetrics.deals.totalCompleted < businessMetrics.deals.exits) {
    issues.push('Total completed deals should be >= exits');
  }
  
  if (businessMetrics.timeline.fullProcess.months * 4 !== businessMetrics.process.totalWeeks) {
    issues.push('Timeline months and process weeks should align');
  }
  
  return issues;
};

export default businessMetrics;
